import { CSSProperties, useState } from "react";
import {
  UseFloatingReturn,
  autoUpdate,
  flip,
  offset,
  size,
  useFloating as useFloatingBase,
  useFloatingNodeId,
} from "@floating-ui/react";
import { Props } from "./UseFloating.Parts";

export const useFloating = ({
  anchor,
  open,
  onOpenChange,
  placement = "bottom-start",
  minHeight,
  minWidth,
  matchAnchorWidth,
  offset: offsetValue = 16,
  nodeId: nodeIdProp,
}: Props): // Explicit return type required to avoid TS2742 (transitive @floating-ui/react-dom reference)
Pick<UseFloatingReturn, "refs" | "floatingStyles" | "context"> & {
  availableHeight: number | undefined;
} => {
  const internalNodeId = useFloatingNodeId();
  const nodeId = nodeIdProp || internalNodeId;
  const [availableHeight, setAvailableHeight] = useState<number | undefined>();

  const { refs, floatingStyles, context } = useFloatingBase({
    nodeId,
    placement,
    open,
    onOpenChange,
    elements: {
      reference: anchor,
    },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetValue),
      flip({
        fallbackStrategy: "bestFit",
        padding: 4,
      }),
      size({
        apply({ elements, availableHeight: ah, availableWidth, rects }) {
          // Bridge the middleware-measured height into React so callers can
          // constrain inner scroll containers (e.g. Dropdown.Content) that
          // the middleware can't reach directly via elements.floating.
          setAvailableHeight(ah);

          const styles: CSSProperties = {
            maxHeight: `${ah}px`,
            maxWidth: `${availableWidth}px`,
          };

          if (minHeight)
            styles.minHeight =
              typeof minHeight === "string" ? minHeight : `${minHeight}rem`;

          if (matchAnchorWidth) styles.width = `${rects.reference.width}px`;
          else if (minWidth)
            styles.minWidth =
              typeof minWidth === "string" ? minWidth : `${minWidth}rem`;

          Object.assign(elements.floating.style, styles);
        },
        padding: 10,
      }),
    ],
  });

  return { refs, floatingStyles, context, availableHeight };
};
